// src/pages/PaymentPage.js
import React, { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { Card, Button, message, Typography, Space, Result } from 'antd'
import styled from 'styled-components'
import api from '../utils/api'
import { CLIENT_PUBLIC_SITE_URL } from '../utils/clientPublicSiteUrl'

const { Title, Text } = Typography

const PaymentContainer = styled.div`
  margin: auto;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f7fa;
  position: relative;
`

const PaymentCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  width: 100%;
  max-width: 400px;
`

const Watermark = styled.div`
  position: fixed;
  bottom: 20px;
  left: 0;
  right: 0;
  text-align: center;
  pointer-events: none;
  z-index: 1;
`

const WatermarkText = styled(Text)`
  color: rgba(0, 0, 0, 0.1);
  font-size: 24px;
  font-weight: 500;
  letter-spacing: 2px;
  text-transform: uppercase;
`

const PaymentPage = () => {
  const { orderSn } = useParams()
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(false)
  const [paymentInfo, setPaymentInfo] = useState(null)
  const [error, setError] = useState(null)
  const [businessInfo, setBusinessInfo] = useState(null)
  const [pendingHint, setPendingHint] = useState('')
  const [retryInSeconds, setRetryInSeconds] = useState(0)

  const amount = searchParams.get('amount')

  useEffect(() => {
    let cancelled = false
    let refreshTimer = null
    let countdownTimer = null

    const normalizeStatus = (status) => String(status || '').toUpperCase()
    const isCompletedStatus = (s) =>
      s === 'COMPLETED' || s === 'PAID' || s === 'SUCCESS'

    const scheduleRetry = (seconds = 4) => {
      setRetryInSeconds(seconds)
      countdownTimer = setInterval(() => {
        setRetryInSeconds((prev) => {
          if (prev <= 1) {
            clearInterval(countdownTimer)
            return 0
          }
          return prev - 1
        })
      }, 1000)
      refreshTimer = setTimeout(() => {
        checkPaymentStatus()
      }, seconds * 1000)
    }

    const checkPaymentStatus = async () => {
      if (!orderSn) {
        setError('無效的支付連結')
        return
      }

      try {
        setLoading(true)
        // 以 query 版為主，並保留 path 版 fallback 相容
        let response = null
        try {
          response = await api.get('/api/payments/quick-payment/result', {
            params: { order_sn: orderSn }
          })
        } catch (queryErr) {
          response = await api.get(`/api/payments/quick-payment/result/${orderSn}`)
        }
        if (cancelled) return

        const payload = response?.data?.data || {}
        const status = normalizeStatus(payload.status)
        const { payment_data, business_name } = payload

        if (isCompletedStatus(status)) {
          setError('此訂單已完成支付')
          return
        }

        if (status === 'EXPIRED') {
          setError('支付連結已過期')
          return
        }

        if (status === 'PENDING') {
          setError(null)
          setPendingHint(payload.hint || '付款結果尚在確認中，請稍候。')
          scheduleRetry(4)
        } else {
          setPendingHint('')
          setRetryInSeconds(0)
        }

        setPaymentInfo(payment_data)
        setBusinessInfo(business_name)
      } catch (error) {
        if (cancelled) return
        console.error('獲取支付信息失敗:', error)
        setError(
          error.response?.data?.message || '獲取支付信息失敗，請稍後再試'
        )
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    checkPaymentStatus()
    return () => {
      cancelled = true
      if (refreshTimer) clearTimeout(refreshTimer)
      if (countdownTimer) clearInterval(countdownTimer)
    }
  }, [orderSn])

  const handlePayment = async () => {
    try {
      setLoading(true)
      // 獲取支付表單數據
      const response = await api.get(
        `/api/payments/quick-payment/${orderSn}?amount=${amount}`
      )

      if (!response.data.success) {
        throw new Error(response.data.message || '獲取支付資料失敗')
      }

      const { payment_data, merchant_id } = response.data.data

      // 調試：檢查支付資料
      console.log('前端支付資料:', {
        payment_data,
        merchant_id
      })

      // 驗證支付資料
      if (!payment_data || !merchant_id) {
        throw new Error('支付資料不完整')
      }

      // 建立隱藏的表單
      const form = document.createElement('form')
      form.method = 'POST'
      // 測試環境
      // form.action = 'https://ccore.newebpay.com/MPG/mpg_gateway'

      // 正式環境
      form.action = 'https://core.newebpay.com/MPG/mpg_gateway'
      form.style.display = 'none'

      // 添加支付資料欄位
      Object.entries(payment_data).forEach(([key, value]) => {
        if (!value) {
          console.error(`表單欄位 ${key} 的值為空`)
          return
        }
        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = key
        input.value = value
        form.appendChild(input)
      })

      // 添加商家ID
      const merchantInput = document.createElement('input')
      merchantInput.type = 'hidden'
      merchantInput.name = 'MerchantID'
      merchantInput.value = merchant_id
      form.appendChild(merchantInput)

      // 添加版本號
      const versionInput = document.createElement('input')
      versionInput.type = 'hidden'
      versionInput.name = 'Version'
      versionInput.value = '2.0' // 藍新金流要求的版本號
      form.appendChild(versionInput)

      // 調試：檢查最終表單
      console.log('最終表單 HTML:', form.outerHTML)

      // 添加表單到文檔並提交
      document.body.appendChild(form)
      form.submit()
    } catch (error) {
      console.error('支付處理失敗:', error)
      message.error(error.message || '支付處理失敗，請稍後再試')
    } finally {
      setLoading(false)
    }
  }

  if (error) {
    return (
      <PaymentContainer>
        <Result
          status='error'
          title='支付失敗'
          subTitle={error}
          extra={[
            <Button
              type='primary'
              key='back'
              onClick={() => {
                window.location.href = CLIENT_PUBLIC_SITE_URL
              }}
            >
              返回首頁
            </Button>
          ]}
        />
      </PaymentContainer>
    )
  }

  if (!orderSn) {
    return null
  }

  return (
    <PaymentContainer>
      <PaymentCard>
        <Space direction='vertical' size='large' style={{ width: '100%' }}>
          {pendingHint && (
            <Result
              status='info'
              title='付款確認中'
              subTitle={`${pendingHint}（約 ${retryInSeconds} 秒後自動重查）`}
            />
          )}
          {businessInfo && (
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <Text style={{ fontSize: '16px', color: '#666' }}>
                {businessInfo}
              </Text>
            </div>
          )}

          <Title level={3} style={{ textAlign: 'center' }}>
            確認支付
          </Title>

          <div style={{ textAlign: 'center' }}>
            <Text style={{ fontSize: '32px', fontWeight: 'bold' }}>
              NT$ {Number(amount).toLocaleString()}
            </Text>
          </div>

          <Button
            type='primary'
            size='large'
            block
            onClick={handlePayment}
            loading={loading}
            style={{ height: '48px', fontSize: '18px' }}
          >
            確認支付
          </Button>

          <Text
            type='secondary'
            style={{ textAlign: 'center', display: 'block' }}
          >
            點擊確認後將跳轉至藍新支付頁面
          </Text>
        </Space>
      </PaymentCard>
      <Watermark>
        <WatermarkText>Beautefeel</WatermarkText>
      </Watermark>
    </PaymentContainer>
  )
}

export default PaymentPage
